import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';

const HeaderBar = styled.header`
  position: sticky;
  top: 0;
  z-index: 1000;
  background: rgba(238, 240, 235, 0.97);
  border-bottom: 1px solid var(--border-light);
  height: 72px;
  display: flex;
  align-items: center;
`;

const Container = styled.div`
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  padding: 0 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const LogoLink = styled(Link)`
  text-decoration: none !important;
  display: flex;
  align-items: baseline;
  gap: 6px;
  color: var(--spruce);
  transition: opacity 0.3s ease;

  &:hover {
    opacity: 0.8;
  }
`;

const LogoBold = styled.span`
  font-family: 'Lora', serif;
  font-size: 1.4rem;
  font-weight: 700;
  color: var(--spruce-dark);
`;

const LogoLight = styled.span`
  font-family: 'Lora', serif;
  font-size: 1.4rem;
  font-weight: 400;
  color: var(--spruce);
`;

const DesktopNav = styled.nav`
  display: flex;
  align-items: center;
  gap: 28px;

  @media (max-width: 899px) {
    display: none;
  }
`;

const NavLink = styled(Link)`
  text-decoration: none !important;
  font-family: 'Inter', sans-serif;
  font-size: 0.75rem;
  font-weight: ${({ $active }) => ($active ? '600' : '400')};
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: ${({ $active }) => ($active ? 'var(--spruce-dark)' : 'var(--text-muted)')};
  padding: 4px 0;
  position: relative;
  transition: color 0.2s ease;

  &::after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    width: 100%;
    height: 1.5px;
    background: var(--sage);
    transform: scaleX(${({ $active }) => ($active ? '1' : '0')});
    transform-origin: center;
    transition: transform 0.2s ease;
  }

  &:hover {
    color: var(--spruce-dark);

    &::after {
      transform: scaleX(1);
    }
  }
`;

const Hamburger = styled.button`
  display: none;
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  z-index: 1001;
  position: relative;
  width: 32px;
  height: 24px;

  @media (max-width: 899px) {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }
`;

const HamburgerLine = styled.span`
  display: block;
  width: 22px;
  height: 2px;
  background: var(--spruce-dark);
  position: absolute;
  left: 5px;
  transition: transform 0.3s ease, opacity 0.3s ease;

  &:nth-child(1) {
    top: ${({ $open }) => ($open ? '11px' : '4px')};
    transform: ${({ $open }) => ($open ? 'rotate(45deg)' : 'rotate(0)')};
  }

  &:nth-child(2) {
    top: 11px;
    opacity: ${({ $open }) => ($open ? '0' : '1')};
  }

  &:nth-child(3) {
    top: ${({ $open }) => ($open ? '11px' : '18px')};
    transform: ${({ $open }) => ($open ? 'rotate(-45deg)' : 'rotate(0)')};
  }
`;

const MobileNav = styled.div`
  display: none;

  @media (max-width: 899px) {
    display: block;
    position: fixed;
    top: 72px;
    left: 0;
    right: 0;
    background: var(--cream);
    border-bottom: 1px solid var(--border-light);
    transform: translateY(${({ $open }) => ($open ? '0' : '-100%')});
    opacity: ${({ $open }) => ($open ? '1' : '0')};
    visibility: ${({ $open }) => ($open ? 'visible' : 'hidden')};
    transition: transform 0.3s ease, opacity 0.3s ease, visibility 0.3s ease;
    z-index: 999;
  }
`;

const MobileNavInner = styled.div`
  display: flex;
  flex-direction: column;
  padding: 8px 0;
`;

const MobileNavLink = styled(Link)`
  text-decoration: none !important;
  font-family: 'Inter', sans-serif;
  font-size: 0.8rem;
  font-weight: ${({ $active }) => ($active ? '600' : '400')};
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${({ $active }) => ($active ? 'var(--spruce-dark)' : 'var(--text-body)')};
  padding: 14px 24px;
  border-bottom: 1px solid var(--border-light);
  transition: background 0.2s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: var(--frost);
  }
`;

const navItems = [
  { label: 'Home', path: '/' },
  { label: 'About', path: '/about' },
  { label: 'Services', path: '/services' },
  { label: 'Who I Work With', path: '/who-i-work-with' },
  { label: 'Fees', path: '/fees' },
  { label: 'FAQs', path: '/faqs' },
  { label: 'Contact', path: '/contact' },
];

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const { pathname } = useLocation();

  const handleLinkClick = () => {
    setMenuOpen(false);
  };

  return (
    <>
      <HeaderBar>
        <Container>
          <LogoLink to="/" onClick={handleLinkClick}>
            <LogoBold>Blue Spruce</LogoBold>
            <LogoLight>Therapy</LogoLight>
          </LogoLink>

          <DesktopNav>
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                $active={pathname === item.path}
              >
                {item.label}
              </NavLink>
            ))}
          </DesktopNav>

          <Hamburger
            onClick={() => setMenuOpen((prev) => !prev)}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          >
            <HamburgerLine $open={menuOpen} />
            <HamburgerLine $open={menuOpen} />
            <HamburgerLine $open={menuOpen} />
          </Hamburger>
        </Container>
      </HeaderBar>

      <MobileNav $open={menuOpen}>
        <MobileNavInner>
          {navItems.map((item) => (
            <MobileNavLink
              key={item.path}
              to={item.path}
              $active={pathname === item.path}
              onClick={handleLinkClick}
            >
              {item.label}
            </MobileNavLink>
          ))}
        </MobileNavInner>
      </MobileNav>
    </>
  );
};

export default Header;
